// #tag: AUTHENTICATION AND EXAM BOOKMARK CONTEXT PROVIDER
// #purpose: Coordinates student and staff authentication state, OAuth flows, SSO sessions, and persistent exam bookmarks.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { AuthUser, ExamCategory, SavedExam } from '../types';
import {
  clearSession,
  fetchCurrentUser,
  googleSignInRequest,
  loginRequest,
  logoutRequest,
  phoneSignInRequest,
  registerRequest,
} from '../api/auth';
import { followExam, fetchFollowedExams, syncFollowedExams, unfollowExam } from '../api/notifications';
import { playUiSound } from '../lib/uiSound';
import { analyticsEvents, signInWithGoogle } from '../lib/firebase';
import { canAccessAdmin, isSuperAdmin as isSuperAdminRole, usesAdminConsoleOnly as usesAdminConsoleOnlyRole } from '../lib/adminAccess';

type AuthStatus = 'loading' | 'authenticated' | 'guest';

interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  isSignedIn: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  usesAdminConsoleOnly: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (name: string, email: string, password: string) => Promise<AuthUser>;
  loginWithGoogle: () => Promise<AuthUser>;
  loginWithPhone: (idToken: string, name?: string) => Promise<AuthUser>;
  logout: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// #tag: AUTHENTICATION CONTEXT PROVIDER COMPONENT
// #purpose: Manages live user authentication session, SSO cookie verification, and role privileges.
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  // Always start loading — session may live only in the shared SSO cookie.
  const [status, setStatus] = useState<AuthStatus>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchCurrentUser();
        if (cancelled) return;
        setUser(me);
        setStatus('authenticated');
      } catch {
        if (cancelled) return;
        clearSession();
        setUser(null);
        setStatus('guest');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const me = await loginRequest(email, password);
    setUser(me);
    setStatus('authenticated');
    analyticsEvents.login('email');
    return me;
  }, []);

  const register = useCallback(async (name: string, email: string, password: string) => {
    const me = await registerRequest(name, email, password);
    setUser(me);
    setStatus('authenticated');
    analyticsEvents.signUp('email');
    return me;
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const idToken = await signInWithGoogle();
    const me = await googleSignInRequest(idToken);
    setUser(me);
    setStatus('authenticated');
    analyticsEvents.login('google');
    return me;
  }, []);

  const loginWithPhone = useCallback(async (idToken: string, name?: string) => {
    const me = await phoneSignInRequest(idToken, name);
    setUser(me);
    setStatus('authenticated');
    analyticsEvents.login('phone');
    return me;
  }, []);

  const logout = useCallback(() => {
    void logoutRequest();
    clearSession();
    setUser(null);
    setStatus('guest');
    analyticsEvents.logout();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isAuthenticated: status === 'authenticated',
      isSignedIn: status === 'authenticated',
      isAdmin: canAccessAdmin(user?.role),
      isSuperAdmin: isSuperAdminRole(user?.role),
      usesAdminConsoleOnly: usesAdminConsoleOnlyRole(user?.role),
      login,
      register,
      loginWithGoogle,
      loginWithPhone,
      logout,
      signOut: logout,
    }),
    [user, status, login, register, loginWithGoogle, loginWithPhone, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// #tag: AUTHENTICATION STATE HOOK
// #purpose: Resolves the current user's authentication state, role permissions, and sign-in handlers.
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

const SAVED_EXAMS_KEY = 'wfx-results-saved-exams';

function readSavedExamsFromStorage(): SavedExam[] {
  try {
    const raw = localStorage.getItem(SAVED_EXAMS_KEY);
    if (raw) return JSON.parse(raw) as SavedExam[];
  } catch {
    /* ignore */
  }
  return [];
}

function toFollowPayload(exam: SavedExam) {
  return {
    examId: exam.examId || exam.id,
    examTitle: exam.examTitle || exam.title,
    organization: exam.organization,
    examCategory: exam.examCategory,
    followedAt: exam.followedAt || exam.savedAt,
  };
}

function fromFollowRow(row: SavedExam | {
  examId?: string;
  id?: string;
  examTitle?: string;
  title?: string;
  organization?: string | null;
  examCategory?: string | null;
  followedAt?: string;
  savedAt?: string;
}): SavedExam {
  const examId = row.examId || row.id || '';
  const examTitle = row.examTitle || row.title || '';
  return {
    id: examId,
    examId,
    title: examTitle,
    examTitle,
    organization: row.organization || '',
    examCategory: (row.examCategory || 'SSC') as ExamCategory,
    savedAt: row.followedAt || row.savedAt || new Date().toISOString(),
    followedAt: row.followedAt,
  };
}

interface SavedExamsContextValue {
  savedExams: SavedExam[];
  isSaved: (examId: string) => boolean;
  toggleSave: (exam: SavedExam) => void;
}

const SavedExamsContext = createContext<SavedExamsContextValue | null>(null);

// #tag: SAVED EXAMS CONTEXT PROVIDER COMPONENT
// #purpose: Manages local bookmark storage and bidirectional synchronization with server-side exam subscriptions.
export function SavedExamsProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [savedExams, setSavedExams] = useState<SavedExam[]>(readSavedExamsFromStorage);
  const syncedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(SAVED_EXAMS_KEY, JSON.stringify(savedExams));
  }, [savedExams]);

  useEffect(() => {
    if (!isAuthenticated) {
      syncedRef.current = false;
      return;
    }
    if (syncedRef.current) return;

    let cancelled = false;
    (async () => {
      try {
        const localExams = readSavedExamsFromStorage();
        if (localExams.length > 0) {
          await syncFollowedExams(localExams.map(toFollowPayload));
        }
        const res = await fetchFollowedExams();
        if (!cancelled) {
          setSavedExams(res.data.map((row) => fromFollowRow(row as Parameters<typeof fromFollowRow>[0])));
        }
      } catch {
        /* keep local follows */
      } finally {
        if (!cancelled) syncedRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const isSaved = useCallback(
    (examId: string) => savedExams.some((e) => e.id === examId),
    [savedExams],
  );

  const toggleSave = useCallback(
    (exam: SavedExam) => {
      const examId = exam.id;
      const exists = savedExams.some((e) => e.id === examId);
      const nextExam = { ...exam, savedAt: exam.savedAt || new Date().toISOString() };

      setSavedExams((prev) => {
        if (exists) return prev.filter((e) => e.id !== examId);
        return [nextExam, ...prev];
      });

      playUiSound(exists ? 'toggle' : 'success');

      if (isAuthenticated) {
        const payload = toFollowPayload(nextExam);
        (async () => {
          try {
            if (exists) await unfollowExam(examId);
            else await followExam(nextExam);
          } catch {
            /* local state already updated */
          }
        })();
      }
    },
    [savedExams, isAuthenticated],
  );

  return (
    <SavedExamsContext.Provider value={{ savedExams, isSaved, toggleSave }}>
      {children}
    </SavedExamsContext.Provider>
  );
}

// #tag: SAVED EXAMS HOOK
// #purpose: Accesses saved exam bookmarks and provides toggle actions for UI components.
export function useSavedExams(): SavedExamsContextValue {
  const ctx = useContext(SavedExamsContext);
  if (!ctx) throw new Error('useSavedExams must be used within SavedExamsProvider');
  return ctx;
}
